import axios from 'axios';
import type { UserProfile } from '../Data/typeusers';
import { clearAllCache } from "../Data/dataCache";

interface StockData {
    id: number;
    indiceId: number;
    currentPrice: number;
    prevPrice: number;
    open: number;
    high: number;
    low: number;
    date: Date;
    isIncreasing: boolean;
    change: number;
    changePercent: number;
    volume: number;
    label: boolean;
    sma14: number;
    sma14Display: string;
    rsi14: number;
    rsi14Display: string;
    futurePrice: number;
    ema14: number;
    bollingerUpper: number;
    bollingerLower: number;
    macd: number;
    averageVolume: number;

    // Prédiction historique J+1
    predictedIsIncreasing: boolean | null;
    probability: number;
    raccomandation: string;

    // Prédiction historique mouvement fort J+5
    strongMoveProbability: number | null;

    // Réalité connue après J+5
    futureReturn5D: number | null;
    strongMove: boolean | null;
}

interface IndiceDTOList {
    id: number;
    symbol: string;
    name: string;
    regularMarketPrice: number;
    regularMarketChange: number;
    regularMarketOpen: number;
    regularMarketPreviousClose: number;
    regularMarketDayHigh: number;
    regularMarketDayLow: number;
    regularMarketChangePercent: number;
    regularMarketVolume: number;
    quoteType: string;
    exchange: string;
    exchangeTimezoneName: string;
    exchangeTimezoneShortName: string;
    bourse: string;
    datesExercicesFinancieres: Date[];
    label: boolean;
    isIncreasing: boolean;
    probability: number;
    raccomandation: string;
    strongMove: boolean;
    strongMoveProbability: number;
    analysis: Record<string, number>;
    dateUpdated: Date;
    datePrevision: Date;
}

interface IndiceDTO {
    id: number;
    symbol: string;
    name: string;
    regularMarketPrice: number;
    regularMarketChange: number;
    regularMarketOpen: number;
    regularMarketPreviousClose: number;
    regularMarketDayHigh: number;
    regularMarketDayLow: number;
    regularMarketChangePercent: number;
    regularMarketVolume: number;
    quoteType: string;
    exchange: string;
    exchangeTimezoneName: string;
    exchangeTimezoneShortName: string;
    bourse: string;
    datesExercicesFinancieres: Date[];
    label: boolean;
    isIncreasing: boolean;
    probability: number;
    raccomandation: string;
    strongMove: boolean;
    strongMoveProbability: number;
    analysis: Record<string, number>;
    dateUpdated: Date;
    datePrevision: Date;
    trainingData: StockData[];
}

interface LoginModel {
  userName: string;
  password: string;
}

// const API_BASE_URL = 'http://localhost:5184/'; // Remplacez par l'URL de votre API
// const API_BASE_URL = 'v/'; // Remplacez par l'URL de votre API
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://bourse_backend.railway.internal/'; // Valeur par défaut si la variable n'est pas définie
const API_BASE_URL = process.env.REACT_APP_API_BOURSE_URL; // Valeur par défaut si la variable n'est pas définie
// const IDENTITY_BASE_URL = 'http://localhost:5235/'; // Remplacez par l'URL de votre IDENTITY SERVER
// const IDENTITY_BASE_URL = 'https://localhost:7248/'; // Remplacez par l'URL de votre IDENTITY SERVER
// const IDENTITY_BASE_URL = process.env.REACT_APP_IDENTITY_BASE_URL || 'http://bourse_identityserver.railway.internal/'; // Valeur par défaut si la variable n'est pas définie
const IDENTITY_BASE_URL = process.env.REACT_APP_API_IDENTITY_URL; // Valeur par défaut si la variable n'est pas définie

if (!API_BASE_URL || !IDENTITY_BASE_URL) {
  throw new Error("URL d'API manquantes (variables d'environnement Railway non définies)");
}

const httpclientApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 100000
});

const httpclientIdentity = axios.create({
  baseURL: IDENTITY_BASE_URL,
  timeout: 100000
});

// Ajouter un interceptor pour inclure automatiquement le token JWT
httpclientApi.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token && typeof token === 'string' && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Ajouter un interceptor pour inclure automatiquement le token JWT
httpclientIdentity.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Gestion globale d'une session expirée
let isRedirectingToLogin = false;


const handleUnauthorized = (error: unknown) => {

  if (
    !axios.isAxiosError(error) ||
    error.response?.status !== 401
  ) {
    return Promise.reject(error);
  }

  // Si aucun token n'existe, le visiteur est simplement anonyme.
  // Il doit pouvoir consulter les pages publiques.
  const token = sessionStorage.getItem('token');

  if (!token) {
    return Promise.reject(error);
  }

  // Évite les redirections répétées.
  if (!isRedirectingToLogin) {
    isRedirectingToLogin = true;

    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');

    void clearAllCache().finally(() => {

      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      } else {
        isRedirectingToLogin = false;
      }

    });
  }

  return Promise.reject(error);
};

httpclientApi.interceptors.response.use(
  response => response,
  handleUnauthorized
);

httpclientIdentity.interceptors.response.use(
  response => response,
  handleUnauthorized
);

httpclientApi.defaults.headers.post['Content-Type'] = 'application/json';

// Déclaration du type de la réponse
interface PaginatedResponse {
    items: IndiceDTOList[];
    totalPages: number;
  }

  // Fonction pour récupérer l'Agenda
  export const getAgendaSemaine = async (): Promise<IndiceDTOList[]> => {
    try {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1); // minuit
      const end = new Date(start);
      end.setDate(end.getDate() + 6);

      const startStr = start.toISOString();
      const endStr = end.toISOString();

      const response = await httpclientApi.get<IndiceDTOList[]>(
        `Bourse/agenda?start=${startStr}&end=${endStr}`
      );
  
      return response.data;
    } catch (error) {
      console.error("Erreur lors du chargement de l'agenda :", error);
      throw new Error("Impossible de charger l’agenda de la semaine.");
    }
  };

  
// Fonction pour récupérer les indices avec les filtres, tri, et pagination
export const getIndices = async (
  filtre: string,
  exchangeFiltre: string,
  sortOrder: string,
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse> => { // Précise le type de la réponse retournée
  try {
    const params = new URLSearchParams();
    if (filtre) params.append('filtre', filtre);
    if (exchangeFiltre) params.append('exchangeFiltre', exchangeFiltre);
    if (sortOrder) params.append('sortOrder', sortOrder);
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());

    // Indication explicite du type de réponse attendue
    const response = await httpclientApi.get<PaginatedResponse>(`Bourse/IndexDTOApi?${params.toString()}`);

    return response.data; // TypeScript sait maintenant que c'est de type PaginatedResponse
  } catch (error) {
    console.error('Erreur de chargement des indices:', error);
    throw new Error('Erreur de chargement des indices');
  }
};

// Fonction pour récupérer les indices avec les filtres, tri, et pagination
export const getForecasts = async (
  filtre: string,
  exchangeFiltre: string,
  sortOrder: string,
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse> => { // Précise le type de la réponse retournée
  try {
    const params = new URLSearchParams();
    if (filtre) params.append('filtre', filtre);
    if (exchangeFiltre) params.append('exchangeFiltre', exchangeFiltre);
    if (sortOrder) params.append('sortOrder', sortOrder);
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());


    // Indication explicite du type de réponse attendue
    const response = await httpclientApi.get<PaginatedResponse>(`Bourse/ForecastsDTOApi?${params.toString()}`);
    return response.data; // TypeScript sait maintenant que c'est de type PaginatedResponse
  } catch (error) {
    console.error('Erreur de chargement des indices:', error);
    throw new Error('Erreur de chargement des indices');
  }
};

// Fonction pour récupérer les détails d'un indice spécifique
export const getIndiceDetails = async (symbol: string, returnUrl: string = "list"): Promise<IndiceDTO> => {
  try {
    const response = await httpclientApi.get<IndiceDTO>(`Bourse/DetailsDTOApi/${symbol}?returnUrl=${returnUrl}`); // Remplace `/api/indices/${symbol}` par l'URL de ton API
    
    return response.data; // Retourne l'objet IndiceDTO

  } catch (error) {
    console.error('Erreur dans la récupération des détails de l\'indice:', error);
    throw error; // Lance l'erreur pour gestion ultérieure
  }
};

export const refreshHeaderLogin = () => {
  const event = new Event('refresh-login');
  window.dispatchEvent(event);
};

export const login = async (credentials: LoginModel): Promise<{ token: string; user: UserProfile }> => {
  try {
    const response = await httpclientIdentity.post<{ token: string; user: UserProfile }>('auth/login', credentials);
    const { token, user } = response.data;

    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(user)); // <<< on ajoute ça

    await clearAllCache();

    return { token, user };
  } catch (error) {
    console.error('Erreur de login:', error);
    throw new Error('Erreur de login');
  }
};

interface RegisterResponse {
  success: boolean;
  userId?: string;
  message?: string;
}

// Fonction d'enregistrement dans dataApi.ts
export const registerUser = async (formData: any): Promise<RegisterResponse> => {
  try {
    const response = await httpclientIdentity.post('auth/register', formData); // L'URL d'enregistrement

    // Vérifie si la réponse est celle attendue (création réussie)
    if (response.status === 200 || response.status === 201) {
      return response.data;
    }

    // Si le statut n'est ni 200 ni 201, c'est une erreur
    throw new Error('Erreur lors de la création de l\'utilisateur');
  } catch (error: any) {
    // Affiche les erreurs détaillées si disponibles
    if (error.response) {
      console.error("Erreur de la réponse : ", error.response);
      return { success: false, message: error.response.data.message || 'Erreur lors de l\'enregistrement, veuillez réessayer.' };
    } else if (error.request) {
      console.error("Aucune réponse du serveur : ", error.request);
      return { success: false, message: 'Erreur de communication avec le serveur' };
    } else {
      console.error("Erreur inconnue : ", error.message);
      return { success: false, message: 'Erreur lors de l\'enregistrement, veuillez réessayer.' };
    }
  }
};

export const getCurrentUser = async () => {
  const response = await httpclientIdentity.get("auth/me");
  return response.data;
};

interface VerifyEmailRequest {
  email: string;
  code: string;
}

interface VerifyEmailResponse {
  success: boolean;
  message?: string;
}

export const verifyEmail = async (
  payload: VerifyEmailRequest
): Promise<VerifyEmailResponse> => {
  try {
    const response = await httpclientIdentity.post<VerifyEmailResponse>(
      'auth/verify-email',
      payload
    );

    return response.data;
  } catch (error: any) {
    console.error("Erreur verify email:", error);

    return {
      success: false,
      message:
        error.response?.data?.message ||
        "Erreur lors de la vérification du code",
    };
  }
};

export const resendVerificationCode = async (
  email: string
): Promise<VerifyEmailResponse> => {
  try {
    const response = await httpclientIdentity.post<VerifyEmailResponse>(
      'auth/resend-code',
      { email }
    );

    return response.data;
  } catch (error: any) {
    console.error("Erreur resend code:", error);

    return {
      success: false,
      message:
        error.response?.data?.message ||
        "Erreur lors de l'envoi du code",
    };
  }
};

interface ContactRequest {
  name: string;
  email: string;
  message: string;
}

interface ContactResponse {
  success: boolean;
  message?: string;
}

export const sendContactMessage = async (
  payload: ContactRequest
): Promise<ContactResponse> => {
  try {
    const response = await httpclientIdentity.post<ContactResponse>(
      "auth/contact-send",
      payload
    );

    return response.data;
  } catch (error: any) {
    console.error("Erreur envoi contact:", error);

    return {
      success: false,
      message:
        error.response?.data?.message ||
        "Erreur lors de l'envoi du message",
    };
  }
};

export const forgotPassword = async (email: string): Promise<{ success: boolean }> => {
  const response = await httpclientIdentity.post(
    "auth/forgot-password", { email });
    
  return response.data;
};

export const resetPassword = async (
  email: string,
  token: string,
  newPassword: string
): Promise<{ success: boolean }> => {
  const response = await httpclientIdentity.post(
    "auth/reset-password",
    {
      email,
      token,
      newPassword
    }
  );

  return response.data;
};

// export const createPayPalOrder = async (description: string, amount: string): Promise<{ orderId: string }> => {
//   try {
//     const response = await httpclientApi.post<{ orderId: string }>('Payment/create-order', {
//       description,
//       amount
//     });
//     return response.data;
//   } catch (error) {
//     console.error("Erreur lors de la création de l'ordre PayPal :", error);
//     throw new Error("Impossible de créer l'ordre PayPal.");
//   }
// };

interface CreateSubscriptionRequest {
  subscriber?: {
    givenName: string;
    surname: string;
    email: string;
  };
}

export const createPayPalSubscription = async (
  payload: CreateSubscriptionRequest
): Promise<{ subscriptionId: string }> => {
  try {
    const response = await httpclientIdentity.post<{ subscriptionId: string }>(
      'Payment/create-subscription',
      {
        subscriber: payload.subscriber,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la création de l'abonnement PayPal :", error);
    throw new Error("Impossible de créer l'abonnement PayPal.");
  }
};

interface ActivateSubscriptionRequest {
  userId: string;
  subscriptionId: string;
}

export const activateSubscription = async (payload: ActivateSubscriptionRequest) => {
  try {
    const response = await httpclientIdentity.post('Payment/activate-subscription', payload);
    return response.data;
  } catch (error: any) {
    console.error("Erreur lors de l'activation de l'abonnement :", error);
    throw new Error("Impossible d'activer l'abonnement.");
  }
};

interface CancelSubscriptionRequest {
  userId: string;
}

export const cancelPayPalSubscription = async (payload: CancelSubscriptionRequest) => {
  try {
    const response = await httpclientIdentity.post('Payment/cancel-subscription', payload);
    console.log(response.data);
    return response.data;
  } catch (error: any) {
    console.error("Erreur annulation abonnement :", error);

    throw new Error("Impossible d'annuler l'abonnement.");
  }
};

// GET favorites
export const getFavoriteSymbols = async (): Promise<string[]> => {
    const res = await httpclientIdentity.get<string[]>("favorites");
    return res.data;
};

// ADD favorite
export const addFavorite = async (symbol: string) => {
    await httpclientIdentity.post("favorites", { symbol });
};

// REMOVE favorite
export const removeFavorite = async (symbol: string) => {
    await httpclientIdentity.delete(`favorites/${symbol}`);
};

// TOGGLE (client helper)
export const toggleFavorite = async (symbol: string, isFav: boolean) => {
    if (isFav) {
        await removeFavorite(symbol);
    } else {
        await addFavorite(symbol);
    }
};